import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Input,
  Select,
  Card,
  Typography,
  message,
  Tooltip,
  Badge,
  Row,
  Col,
  Statistic,
  Divider,
  Collapse,
  Alert,
  Progress,
  Descriptions
} from 'antd';
import {
  ExclamationCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  FilterOutlined,
  EyeOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FlagOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { reportReviewService, type PendingReport } from '../services/reportReviewService';
import { useAuthStore } from '../stores/authStore';
import dayjs from 'dayjs';
import './PendingReportsReview.css';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const PendingReportsReview: React.FC = () => {
  const { user } = useAuthStore();

  const [reports, setReports] = useState<PendingReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  
  // 筛选状态
  const [filters, setFilters] = useState<{
    content_type: string;
    priority: number | undefined;
  }>({
    content_type: '',
    priority: undefined
  });

  // 审核模态框状态
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState<PendingReport | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 详情模态框状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [page, filters]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await reportReviewService.getPendingReports({
        page,
        pageSize,
        status: 'pending',
        content_type: filters.content_type || undefined,
        priority: filters.priority
      });

      if (response.success) {
        setReports(response.data.items);
        setTotal(response.data.total);
      } else {
        message.error('获取举报列表失败');
      }
    } catch (error) {
      console.error('获取举报列表失败:', error);
      message.error('获取举报列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (report: PendingReport, action: 'approve' | 'reject') => {
    setSelectedReport(report);
    setReviewAction(action);
    setReviewNotes('');
    setReviewModalVisible(true);
  };

  const handleViewDetail = (report: PendingReport) => {
    setSelectedReport(report);
    setDetailModalVisible(true);
  };

  const submitReview = async () => {
    if (!selectedReport) {
      console.warn('[REVIEW] No report selected');
      return;
    }

    if (submitting) {
      console.warn('[REVIEW] Already submitting, please wait');
      return;
    }

    console.log('[REVIEW] Submitting review:', {
      reportId: selectedReport.id,
      action: reviewAction,
      reviewNotes: reviewNotes
    });

    setSubmitting(true);

    try {
      const response = await reportReviewService.reviewReport({
        reportId: selectedReport.id,
        action: reviewAction,
        reviewNotes: reviewNotes
      });

      console.log('[REVIEW] Review response:', response);

      if (response.success) {
        // 根据审核决策显示具体的操作结果提示
        const successMessage = reviewAction === 'approve'
          ? '人工审核完成：举报成立，已删除该内容'
          : '人工审核完成：举报不成立，内容无违规';

        message.success(successMessage);

        // 刷新列表
        console.log('[REVIEW] Refreshing reports list...');
        await fetchReports();

        // 确保在所有操作完成后关闭模态框并重置状态
        setReviewModalVisible(false);
        setSelectedReport(null);
        setReviewNotes('');
      } else {
        console.error('[REVIEW] Review failed:', response.message);
        message.error(response.message || '审核失败，请稍后重试');
        // 即使失败也关闭对话框
        setReviewModalVisible(false);
        setSelectedReport(null);
        setReviewNotes('');
      }
    } catch (error: any) {
      console.error('[REVIEW] Review error:', error);
      message.error(error.message || '审核失败，请稍后重试');
      // 发生错误时也关闭对话框
      setReviewModalVisible(false);
      setSelectedReport(null);
      setReviewNotes('');
    } finally {
      // 确保 submitting 状态总是被重置
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left' as const
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: number) => (
        <Tag color={reportReviewService.getPriorityColor(priority)}>
          {reportReviewService.getPriorityLabel(priority)} ({priority})
        </Tag>
      ),
      sorter: (a: PendingReport, b: PendingReport) => a.priority - b.priority
    },
    {
      title: '举报类型',
      dataIndex: 'report_type',
      key: 'report_type',
      width: 120,
      render: (type: string) => (
        <Tag color={reportReviewService.getReportTypeColor(type)}>
          {reportReviewService.getReportTypeLabel(type)}
        </Tag>
      )
    },
    {
      title: '内容类型',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 100,
      render: (type: string) => (
        <Tag color="blue">{type === 'story' ? '故事' : '问卷'}</Tag>
      )
    },
    {
      title: '内容ID',
      dataIndex: 'content_id',
      key: 'content_id',
      width: 80
    },
    {
      title: '举报原因',
      dataIndex: 'report_reason',
      key: 'report_reason',
      width: 250,
      render: (text: string) => (
        <Tooltip title={text}>
          <div className="report-reason-cell">
            {text || '-'}
          </div>
        </Tooltip>
      )
    },
    {
      title: '举报者',
      dataIndex: 'reporter_user_id',
      key: 'reporter_user_id',
      width: 150,
      render: (userId: string, record: PendingReport) => (
        <Tooltip title={`用户ID: ${userId}${record.reporter_reputation.score !== null ? ` | 信誉: ${record.reporter_reputation.score}` : ''}`}>
          <div className="reporter-cell">
            <Text ellipsis>{userId}</Text>
            {record.reporter_reputation.score !== null && (
              <Text type="secondary" className="reporter-reputation">
                信誉: {record.reporter_reputation.score}
              </Text>
            )}
          </div>
        </Tooltip>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: PendingReport) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleReview(record, 'approve')}
          >
            批准
          </Button>
          <Button
            danger
            size="small"
            icon={<CloseOutlined />}
            onClick={() => handleReview(record, 'reject')}
          >
            驳回
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic
              title="待审核举报"
              value={total}
              prefix={<FlagOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="当前页"
              value={page}
              suffix={`/ ${Math.ceil(total / pageSize)}`}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="审核员"
              value={user?.username || '-'}
              prefix={<UserOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="更新时间"
              value={dayjs().format('HH:mm:ss')}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>

        <Divider />

        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="内容类型"
            style={{ width: 120 }}
            allowClear
            value={filters.content_type || undefined}
            onChange={(value) => setFilters({ ...filters, content_type: value || '' })}
          >
            <Option value="story">故事</Option>
            <Option value="questionnaire">问卷</Option>
          </Select>

          <Select
            placeholder="优先级"
            style={{ width: 120 }}
            allowClear
            value={filters.priority}
            onChange={(value) => setFilters({ ...filters, priority: value })}
          >
            <Option value={2}>紧急 (≤2)</Option>
            <Option value={4}>高 (≤4)</Option>
            <Option value={6}>中 (≤6)</Option>
            <Option value={10}>低 (≤10)</Option>
          </Select>

          <Button
            icon={<ReloadOutlined />}
            onClick={fetchReports}
          >
            刷新
          </Button>
        </Space>

        <Table
          className="compact-table"
          columns={columns}
          dataSource={reports}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1200 }}
          size="small"
          pagination={{
            current: page,
            total,
            pageSize,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (total) => `共 ${total} 条举报`
          }}
        />
      </Card>

      {/* 审核模态框 */}
      <Modal
        title={
          <Space>
            {reviewAction === 'approve' ? (
              <CheckOutlined style={{ color: '#52c41a' }} />
            ) : (
              <CloseOutlined style={{ color: '#ff4d4f' }} />
            )}
            <span>{reviewAction === 'approve' ? '批准举报' : '驳回举报'}</span>
          </Space>
        }
        open={reviewModalVisible}
        onOk={submitReview}
        onCancel={() => {
          if (!submitting) {
            setReviewModalVisible(false);
            setSelectedReport(null);
            setReviewNotes('');
          }
        }}
        confirmLoading={submitting}
        okText="确认"
        cancelText="取消"
        maskClosable={!submitting}
        closable={!submitting}
      >
        {selectedReport && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Text strong>举报ID: </Text>
              <Text>{selectedReport.id}</Text>
            </div>
            <div>
              <Text strong>举报类型: </Text>
              <Tag color={reportReviewService.getReportTypeColor(selectedReport.report_type)}>
                {reportReviewService.getReportTypeLabel(selectedReport.report_type)}
              </Tag>
            </div>
            <div>
              <Text strong>举报原因: </Text>
              <Paragraph>{selectedReport.report_reason || '-'}</Paragraph>
            </div>
            <div>
              <Text strong>审核备注（可选）: </Text>
              <TextArea
                rows={4}
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="请输入审核备注..."
              />
            </div>
          </Space>
        )}
      </Modal>

      {/* 详情模态框 - 显示被举报内容和举报信息 */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>举报详情审核</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedReport(null);
        }}
        footer={
          selectedReport && (
            <Space>
              <Button onClick={() => {
                setDetailModalVisible(false);
                setSelectedReport(null);
              }}>
                关闭
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => {
                  setDetailModalVisible(false);
                  handleReview(selectedReport, 'approve');
                }}
              >
                批准举报
              </Button>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => {
                  setDetailModalVisible(false);
                  handleReview(selectedReport, 'reject');
                }}
              >
                驳回举报
              </Button>
            </Space>
          )
        }
        width={900}
      >
        {selectedReport && (
          <Collapse defaultActiveKey={['content', 'ai_review', 'report']} style={{ marginBottom: 16 }}>
            {/* 被举报内容区域 */}
            <Panel
              header={
                <Space>
                  <EyeOutlined />
                  <Text strong>被举报内容</Text>
                  <Tag color="blue">{selectedReport.content_type === 'story' ? '故事' : '问卷'}</Tag>
                </Space>
              }
              key="content"
            >
              {selectedReport.reported_content ? (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text strong>标题: </Text>
                    <Title level={5} style={{ margin: '8px 0' }}>
                      {selectedReport.reported_content.title}
                    </Title>
                  </div>
                  <Divider style={{ margin: '12px 0' }} />
                  <div>
                    <Text strong>正文内容: </Text>
                    <Paragraph
                      style={{
                        marginTop: 8,
                        padding: 12,
                        background: '#f5f5f5',
                        borderRadius: 4,
                        maxHeight: 300,
                        overflow: 'auto'
                      }}
                    >
                      {selectedReport.reported_content.content}
                    </Paragraph>
                  </div>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text type="secondary">作者ID: </Text>
                      <Text>{selectedReport.reported_content.author}</Text>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">分类: </Text>
                      <Tag>{selectedReport.reported_content.category}</Tag>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">发布时间: </Text>
                      <Text>{dayjs(selectedReport.reported_content.created_at).format('YYYY-MM-DD HH:mm')}</Text>
                    </Col>
                  </Row>
                  {selectedReport.reported_content.tags && selectedReport.reported_content.tags.length > 0 && (
                    <div>
                      <Text type="secondary">标签: </Text>
                      <Space wrap>
                        {selectedReport.reported_content.tags.map((tag, index) => (
                          <Tag key={index} color="blue">{tag}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Space>
              ) : (
                <Alert
                  message="无法获取被举报内容"
                  description="被举报的内容可能已被删除或不存在"
                  type="warning"
                  showIcon
                />
              )}
            </Panel>

            {/* AI审核结果区域 */}
            <Panel
              header={
                <Space>
                  <RobotOutlined />
                  <Text strong>AI审核结果</Text>
                  {selectedReport.ai_review && (
                    <Tag color={selectedReport.ai_review.review_result === 'violation' ? 'red' : 'green'}>
                      {selectedReport.ai_review.review_result === 'violation' ? '检测到违规' : '未检测到违规'}
                    </Tag>
                  )}
                </Space>
              }
              key="ai_review"
            >
              {selectedReport.ai_review ? (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {/* AI判定结果 */}
                  <Alert
                    message={
                      <Space>
                        <Text strong>AI判定：</Text>
                        <Text>{selectedReport.ai_review.review_result === 'violation' ? '内容违规' : '内容安全'}</Text>
                      </Space>
                    }
                    description={selectedReport.ai_review.reason}
                    type={selectedReport.ai_review.review_result === 'violation' ? 'warning' : 'success'}
                    showIcon
                  />

                  {/* 风险评分和置信度 */}
                  <Row gutter={16}>
                    <Col span={12}>
                      <Card size="small">
                        <Statistic
                          title="风险评分"
                          value={(selectedReport.ai_review.risk_score * 100).toFixed(1)}
                          suffix="%"
                          valueStyle={{
                            color: selectedReport.ai_review.risk_score > 0.7 ? '#cf1322' :
                                   selectedReport.ai_review.risk_score > 0.4 ? '#faad14' : '#3f8600'
                          }}
                        />
                        <Progress
                          percent={parseFloat((selectedReport.ai_review.risk_score * 100).toFixed(1))}
                          strokeColor={
                            selectedReport.ai_review.risk_score > 0.7 ? '#cf1322' :
                            selectedReport.ai_review.risk_score > 0.4 ? '#faad14' : '#3f8600'
                          }
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card size="small">
                        <Statistic
                          title="AI置信度"
                          value={(selectedReport.ai_review.confidence * 100).toFixed(1)}
                          suffix="%"
                          valueStyle={{ color: '#1890ff' }}
                        />
                        <Progress
                          percent={parseFloat((selectedReport.ai_review.confidence * 100).toFixed(1))}
                          strokeColor="#1890ff"
                          showInfo={false}
                        />
                      </Card>
                    </Col>
                  </Row>

                  {/* 违规类型 */}
                  {selectedReport.ai_review.violation_types && selectedReport.ai_review.violation_types.length > 0 && (
                    <div>
                      <Text strong>检测到的违规类型: </Text>
                      <div style={{ marginTop: 8 }}>
                        <Space wrap>
                          {selectedReport.ai_review.violation_types.map((type, index) => (
                            <Tag key={index} color="red">{type}</Tag>
                          ))}
                        </Space>
                      </div>
                    </div>
                  )}

                  {/* AI推荐 */}
                  <div>
                    <Text strong>AI推荐操作: </Text>
                    <Tag color={
                      selectedReport.ai_review.recommendation === 'reject' ? 'red' :
                      selectedReport.ai_review.recommendation === 'review' ? 'orange' : 'green'
                    }>
                      {selectedReport.ai_review.recommendation === 'reject' ? '拒绝' :
                       selectedReport.ai_review.recommendation === 'review' ? '人工审核' : '批准'}
                    </Tag>
                  </div>

                  {/* AI审核详情 */}
                  <Descriptions size="small" column={2} bordered>
                    <Descriptions.Item label="审核时间">
                      {dayjs(selectedReport.ai_review.reviewed_at).format('YYYY-MM-DD HH:mm:ss')}
                    </Descriptions.Item>
                    <Descriptions.Item label="处理状态">
                      <Tag color="blue">已完成</Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Space>
              ) : (
                <Alert
                  message="AI审核未完成"
                  description="该举报尚未进行AI审核，或AI审核失败"
                  type="info"
                  showIcon
                />
              )}
            </Panel>

            {/* 举报信息区域 */}
            <Panel
              header={
                <Space>
                  <FlagOutlined />
                  <Text strong>举报信息</Text>
                  <Tag color={reportReviewService.getPriorityColor(selectedReport.priority)}>
                    优先级: {reportReviewService.getPriorityLabel(selectedReport.priority)}
                  </Tag>
                </Space>
              }
              key="report"
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Row gutter={16}>
                  <Col span={8}>
                    <Text strong>举报ID: </Text>
                    <Text>{selectedReport.id}</Text>
                  </Col>
                  <Col span={8}>
                    <Text strong>举报类型: </Text>
                    <Tag color={reportReviewService.getReportTypeColor(selectedReport.report_type)}>
                      {reportReviewService.getReportTypeLabel(selectedReport.report_type)}
                    </Tag>
                  </Col>
                  <Col span={8}>
                    <Text strong>优先级: </Text>
                    <Tag color={reportReviewService.getPriorityColor(selectedReport.priority)}>
                      {reportReviewService.getPriorityLabel(selectedReport.priority)} ({selectedReport.priority})
                    </Tag>
                  </Col>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
                <div>
                  <Text strong>举报原因: </Text>
                  <Paragraph style={{ marginTop: 8 }}>
                    {selectedReport.report_reason || '（未填写）'}
                  </Paragraph>
                </div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>举报者ID: </Text>
                    <Text>{selectedReport.reporter_user_id}</Text>
                  </Col>
                  <Col span={12}>
                    {selectedReport.reporter_reputation.score !== null && (
                      <>
                        <Text strong>举报者信誉: </Text>
                        <Tag color={
                          selectedReport.reporter_reputation.score >= 80 ? 'green' :
                          selectedReport.reporter_reputation.score >= 60 ? 'blue' :
                          selectedReport.reporter_reputation.score >= 40 ? 'orange' : 'red'
                        }>
                          {selectedReport.reporter_reputation.score} 分 ({selectedReport.reporter_reputation.level})
                        </Tag>
                      </>
                    )}
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="secondary">举报者IP: </Text>
                    <Text>{selectedReport.reporter_ip}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary">举报时间: </Text>
                    <Text>{dayjs(selectedReport.created_at).format('YYYY-MM-DD HH:mm:ss')}</Text>
                  </Col>
                </Row>
              </Space>
            </Panel>
          </Collapse>
        )}
      </Modal>
    </div>
  );
};

export default PendingReportsReview;

